import { Search, User, UserPlus, Loader2, Check } from "lucide-react";
import type { Customer, NewCustomerForm } from "./types";

interface CustomerStepProps {
  customerMode: "search" | "new";
  setCustomerMode: (mode: "search" | "new") => void;
  customerSearch: string;
  setCustomerSearch: (value: string) => void;
  customersLoading: boolean;
  filteredCustomers: Customer[];
  selectedCustomer: Customer | null;
  setSelectedCustomer: (customer: Customer) => void;
  newCustomer: NewCustomerForm;
  setNewCustomer: (value: NewCustomerForm | ((prev: NewCustomerForm) => NewCustomerForm)) => void;
  handleCreateCustomer: () => void;
  createCustomerPending: boolean;
  canProceedToDetails: boolean;
  onBack: () => void;
  onNext: () => void;
}

export default function CustomerStep({
  customerMode,
  setCustomerMode,
  customerSearch,
  setCustomerSearch,
  customersLoading,
  filteredCustomers,
  selectedCustomer,
  setSelectedCustomer,
  newCustomer,
  setNewCustomer,
  handleCreateCustomer,
  createCustomerPending,
  canProceedToDetails,
  onBack,
  onNext,
}: CustomerStepProps) {
  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Customer</h2>

      {/* Mode toggle */}
      <div className="flex gap-2">
        <button
          onClick={() => setCustomerMode("search")}
          className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            customerMode === "search"
              ? "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300"
          }`}
        >
          <User className="h-4 w-4" />
          Existing customer
        </button>
        <button
          onClick={() => setCustomerMode("new")}
          className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            customerMode === "new"
              ? "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300"
          }`}
        >
          <UserPlus className="h-4 w-4" />
          New customer
        </button>
      </div>

      {customerMode === "search" ? (
        <>
          {/* Customer search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              className="input pl-10"
            />
          </div>

          {/* Customers list */}
          {customersLoading ? (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="text-primary-600 h-6 w-6 animate-spin" />
            </div>
          ) : (
            <div className="max-h-64 space-y-2 overflow-y-auto">
              {filteredCustomers.slice(0, 10).map((customer) => (
                <button
                  key={customer.id}
                  onClick={() => setSelectedCustomer(customer)}
                  className={`flex w-full items-center justify-between rounded-lg border p-3 text-left transition-colors ${
                    selectedCustomer?.id === customer.id
                      ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20"
                      : "border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
                  }`}
                >
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {customer.firstName} {customer.lastName}
                    </p>
                    <p className="text-sm text-gray-500">
                      {customer.email}
                      {customer.phone && ` | ${customer.phone}`}
                    </p>
                  </div>
                  {selectedCustomer?.id === customer.id && (
                    <Check className="text-primary-600 h-5 w-5" />
                  )}
                </button>
              ))}
              {filteredCustomers.length === 0 && customerSearch && (
                <p className="py-4 text-center text-gray-500">
                  Customer not found.{" "}
                  <button
                    onClick={() => {
                      setCustomerMode("new");
                      setNewCustomer((prev) => ({
                        ...prev,
                        email: customerSearch.includes("@") ? customerSearch : "",
                      }));
                    }}
                    className="text-primary-600 hover:underline"
                  >
                    Create new
                  </button>
                </p>
              )}
            </div>
          )}
        </>
      ) : (
        /* New customer form */
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              First name *
            </label>
            <input
              type="text"
              value={newCustomer.firstName}
              onChange={(e) => setNewCustomer({ ...newCustomer, firstName: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Last name *
            </label>
            <input
              type="text"
              value={newCustomer.lastName}
              onChange={(e) => setNewCustomer({ ...newCustomer, lastName: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Email *
            </label>
            <input
              type="email"
              value={newCustomer.email}
              onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Phone
            </label>
            <input
              type="tel"
              value={newCustomer.phone}
              onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
              className="input"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Street
            </label>
            <input
              type="text"
              value={newCustomer.street}
              onChange={(e) => setNewCustomer({ ...newCustomer, street: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              City
            </label>
            <input
              type="text"
              value={newCustomer.city}
              onChange={(e) => setNewCustomer({ ...newCustomer, city: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              ZIP code
            </label>
            <input
              type="text"
              value={newCustomer.zip}
              onChange={(e) => setNewCustomer({ ...newCustomer, zip: e.target.value })}
              className="input"
            />
          </div>
          <div className="sm:col-span-2">
            <button
              onClick={handleCreateCustomer}
              disabled={
                !newCustomer.email ||
                !newCustomer.firstName ||
                !newCustomer.lastName ||
                createCustomerPending
              }
              className="btn-primary"
            >
              {createCustomerPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <UserPlus className="mr-2 h-4 w-4" />
              )}
              Create customer
            </button>
          </div>
        </div>
      )}

      {/* Selected customer info */}
      {selectedCustomer && (
        <div className="bg-success-50 dark:bg-success-900/20 rounded-lg p-4">
          <p className="text-success-700 dark:text-success-400 font-medium">
            Selected customer: {selectedCustomer.firstName} {selectedCustomer.lastName} (
            {selectedCustomer.email})
          </p>
        </div>
      )}

      <div className="flex justify-between pt-4">
        <button onClick={onBack} className="btn-ghost">
          Back
        </button>
        <button onClick={onNext} disabled={!canProceedToDetails} className="btn-primary">
          Continue
        </button>
      </div>
    </div>
  );
}
